import { supabase } from "@/integrations/supabase/client";
import type { WeekdayName } from "@/lib/academicCalendar";
import { semesterIdFromLabel } from "@/lib/academicCalendar";

export interface AffectedWindow {
  semester_key: string;
  semester_label: string;
  weekday: WeekdayName;
  change: "created" | "updated" | "deleted";
  before?: { start: string; end: string; weeks: number };
  after?: { start: string; end: string; weeks: number };
  classes: Array<{ id: string; title: string; slug: string | null }>;
  registrations: number;
  interest: number;
}

/**
 * Given semester+weekday windows that are about to change, compute how many
 * classes, paid registrations, and interest forms each change will affect.
 */
export async function computeCalendarImpact(
  changes: Array<{
    semester_key: string;
    semester_label: string;
    weekday: WeekdayName;
    change: "created" | "updated" | "deleted";
    before?: { start: string; end: string; weeks: number };
    after?: { start: string; end: string; weeks: number };
  }>,
): Promise<AffectedWindow[]> {
  if (changes.length === 0) return [];

  const [{ data: classes }, { data: pays }, { data: interest }] = await Promise.all([
    supabase.from("classes").select("id, slug, title, sessions, preferred_days, schedule, status"),
    supabase.from("class_payments").select("class_id, payment_status, hidden"),
    supabase.from("class_interest").select("class_id"),
  ]);

  const payCounts = new Map<string, number>();
  for (const p of pays ?? []) {
    if (p.hidden) continue;
    const s = (p.payment_status ?? "").toLowerCase();
    if (s !== "paid" && s !== "complete") continue;
    const key = String((p as any).class_id ?? "");
    payCounts.set(key, (payCounts.get(key) ?? 0) + 1);
  }
  const intCounts = new Map<string, number>();
  for (const i of interest ?? []) {
    const key = String((i as any).class_id ?? "");
    intCounts.set(key, (intCounts.get(key) ?? 0) + 1);
  }

  return changes.map((c) => {
    const matched = (classes ?? []).filter((cls: any) => {
      if (cls.status === "archived") return false;
      const text = [
        JSON.stringify(cls.sessions ?? ""),
        cls.schedule ?? "",
        cls.preferred_days ?? "",
      ].join(" ").toLowerCase();
      // Must reference this semester
      const sem = semesterIdFromLabel(text);
      if (sem !== c.semester_key) return false;
      // And meet on this weekday
      const wd = c.weekday.toLowerCase();
      return text.includes(wd) || text.includes(wd.slice(0, 3));
    });

    let regs = 0, ints = 0;
    for (const m of matched) {
      const byId = payCounts.get(String(m.id)) ?? 0;
      const bySlug = m.slug ? payCounts.get(String(m.slug)) ?? 0 : 0;
      regs += byId + bySlug;
      ints += (intCounts.get(String(m.id)) ?? 0) + (m.slug ? intCounts.get(String(m.slug)) ?? 0 : 0);
    }

    return {
      semester_key: c.semester_key,
      semester_label: c.semester_label,
      weekday: c.weekday,
      change: c.change,
      before: c.before,
      after: c.after,
      classes: matched.map((m: any) => ({ id: m.id, title: m.title, slug: m.slug })),
      registrations: regs,
      interest: ints,
    };
  });
}
